import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { toast } from 'sonner';
import type { TaskComment } from '@/types/task';

export const useTaskComments = (taskId?: string) => {
  const { userId } = useStableAuth();
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchComments = async () => {
    if (!taskId || !userId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('task_comments')
        .select('*')
        .eq('task_id', taskId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Fetch user profiles separately
      const userIds = data?.map(c => c.user_id) || [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds);

      const formattedData = data?.map(comment => ({
        ...comment,
        user: profiles?.find(p => p.user_id === comment.user_id) ? {
          id: profiles.find(p => p.user_id === comment.user_id)!.user_id,
          full_name: profiles.find(p => p.user_id === comment.user_id)!.full_name,
          avatar_url: profiles.find(p => p.user_id === comment.user_id)?.avatar_url
        } : undefined
      })) || [];
      
      setComments(formattedData);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
      toast.error('Erro ao carregar comentários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [taskId, userId]);

  useEffect(() => {
    if (!taskId) return;

    const channel = supabase
      .channel(`task-comments-${taskId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_comments',
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addComment = async (content: string, mentions: string[] = []) => {
    if (!taskId || !userId) return;

    try {
      // Detectar @mentions no texto (formato: @[Nome](user_id))
      const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
      const detectedMentions: string[] = [];
      let match;
      
      while ((match = mentionRegex.exec(content)) !== null) {
        detectedMentions.push(match[2]); // user_id
      }

      // Combinar com mentions passadas manualmente
      const allMentions = [...new Set([...mentions, ...detectedMentions])];

      // Se tem mentions, tornar tarefa não-privada
      if (allMentions.length > 0) {
        const { data: taskData } = await supabase
          .from('tasks')
          .select('company_id')
          .eq('id', taskId)
          .single();

        if (taskData) {
          await supabase
            .from('tasks')
            .update({ is_private: false })
            .eq('id', taskId);

          // Adicionar colaboradores automaticamente para quem foi mencionado
          // (task_collaborators não tem company_id — schema real verificado em auditoria)
          for (const mentionedUserId of allMentions) {
            try {
              await supabase
                .from('task_collaborators')
                .insert({
                  task_id: taskId,
                  user_id: mentionedUserId,
                  role: 'watcher',
                  added_by: userId,
                  // company_id não existe em task_collaborators — removido (auditoria 2026-06-05)
                });
            } catch {
              // Ignora se colaborador já existe (unique constraint)
            }
          }
        }
      }

      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          user_id: userId,
          content,
          mentions: allMentions
        });

      if (error) throw error;
      
      toast.success('Comentário adicionado');
      await fetchComments();
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      toast.error('Erro ao adicionar comentário');
      throw error;
    }
  };

  const updateComment = async (commentId: string, content: string) => {
    try {
      const { error } = await supabase
        .from('task_comments')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', commentId);

      if (error) throw error;
      
      toast.success('Comentário atualizado');
      await fetchComments();
    } catch (error) {
      console.error('Erro ao atualizar comentário:', error);
      toast.error('Erro ao atualizar comentário');
      throw error;
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('task_comments')
        .update({ 
          deleted_at: new Date().toISOString()
        })
        .eq('id', commentId);

      if (error) throw error;
      
      toast.success('Comentário excluído');
      await fetchComments();
    } catch (error) {
      console.error('Erro ao excluir comentário:', error);
      toast.error('Erro ao excluir comentário');
      throw error;
    }
  };

  return {
    comments,
    loading,
    addComment,
    updateComment,
    deleteComment,
    refetch: fetchComments
  };
};
